import React from "react";
import Nav from "./Navigation.js";

const PUZZLE_IMAGE_URL = "https://pub-cf9e8eda097946d689b15497650e2a50.r2.dev/tempSlider.png";

const CELL_SIZE = 96;

const getShuffledPuzzle = () => {
  const values = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  const randnumforgame = Math.floor(Math.random() * 9) + 1;
  values.splice(randnumforgame, 1);
  const rowOne = [], rowTwo = [], rowThree = [];

  while (values.length) {
    const random = Math.floor(Math.random() * values.length);
    if (rowOne.length < 3) rowOne.push(values.splice(random, 1)[0]);
    else if (rowTwo.length < 3) rowTwo.push(values.splice(random, 1)[0]);
    else rowThree.push(values.splice(random, 1)[0]);
  }

  return [rowOne, rowTwo, rowThree];
};

const flattenArray = (arr) => arr.reduce((flat, sub) => flat.concat(sub), []);

const getInversionsCount = (arr) => {
  arr = flattenArray(arr).filter((n) => n !== 0);
  const inversions = [];
  for (let i = 0; i < arr.length - 1; i++) {
    inversions.push(arr.filter((val, j) => i < j && val < arr[i]).length);
  }
  return inversions.reduce((total, val) => total + val, 0);
};

const isSolvable = (puzzle) => getInversionsCount(puzzle) % 2 === 0;

const getPuzzle = () => {
  let puzzle = getShuffledPuzzle();
  while (!isSolvable(puzzle)) puzzle = getShuffledPuzzle();
  return puzzle;
};

const SOLVED_PUZZLE = [[1, 2, 3], [4, 5, 6], [7, 8, 0]];

export default function SliderGame() {
  const [puzzle, setPuzzle] = React.useState(SOLVED_PUZZLE);
  const [complete, setComplete] = React.useState(false);
  const [moves, setMoves] = React.useState(0);
  const [mode, setMode] = React.useState("numbers"); // "numbers" | "picture"
  const [phase, setPhase] = React.useState("preview"); // "preview" | "playing"

  const movePiece = (x, y) => {
    if (phase !== "playing" || complete) return;
    if (!complete) {
      if (checkNeighbours(x, y) || checkNeighbours(x, y, 2)) {
        const emptySlot = checkNeighbours(x, y) || checkNeighbours(x, y, 2);
        const newPuzzle = puzzle.map((row) => row.slice());

        if (x === emptySlot.x && y < emptySlot.y) {
          newPuzzle[emptySlot.x][emptySlot.y] = puzzle[x][y + 1];
          newPuzzle[x][y + 1] = newPuzzle[x][y];
          newPuzzle[x][y] = 0;
        } else if (x === emptySlot.x && y > emptySlot.y) {
          newPuzzle[emptySlot.x][emptySlot.y] = puzzle[x][y - 1];
          newPuzzle[x][y - 1] = newPuzzle[x][y];
          newPuzzle[x][y] = 0;
        }

        if (y === emptySlot.y && x < emptySlot.x) {
          newPuzzle[emptySlot.x][emptySlot.y] = puzzle[x + 1][y];
          newPuzzle[x + 1][y] = newPuzzle[x][y];
          newPuzzle[x][y] = 0;
        } else if (y === emptySlot.y && x > emptySlot.x) {
          newPuzzle[emptySlot.x][emptySlot.y] = puzzle[x - 1][y];
          newPuzzle[x - 1][y] = newPuzzle[x][y];
          newPuzzle[x][y] = 0;
        }

        setPuzzle(newPuzzle);
        setMoves(moves + 1);
        checkCompletion(newPuzzle);
      }
    }
  };

  const checkCompletion = (puzzle) => {
    const flatArray = flattenArray(puzzle).join("");
    const solution = "123456789";
    let count = 0;
    for (let i = 0; i < 9; i++) {
      if (flatArray[i] === solution[i]) count++;
    }
    if (count === 8) setComplete(true);
  };

  const checkNeighbours = (x, y, d = 1) => {
    const neighbours = [];
    if (puzzle[x][y] !== 0) {
      neighbours.push(puzzle[x - d] && puzzle[x - d][y] === 0 && { x: x - d, y });
      neighbours.push(puzzle[x][y + d] === 0 && { x, y: y + d });
      neighbours.push(puzzle[x + d] && puzzle[x + d][y] === 0 && { x: x + d, y });
      neighbours.push(puzzle[x][y - d] === 0 && { x, y: y - d });
    }
    return neighbours.find((el) => typeof el === "object");
  };

  const resetPuzzle = () => {
    setComplete(false);
    setPuzzle(getPuzzle());
    setMoves(0);
    setPhase("playing");
  };

  const renderTile = (col, i, j) => {
    const isEmpty = col === 0;

    if (isEmpty) {
      if ((complete || phase === "preview") && mode === "picture" && PUZZLE_IMAGE_URL) {
        const solvedNum = i * 3 + j + 1;
        const tileRow = Math.floor((solvedNum - 1) / 3);
        const tileCol = (solvedNum - 1) % 3;
        return (
          <div
            key={`${i}-${j}`}
            style={{
              width: CELL_SIZE,
              height: CELL_SIZE,
              backgroundImage: `url(${PUZZLE_IMAGE_URL})`,
              backgroundSize: `${3 * CELL_SIZE}px ${3 * CELL_SIZE}px`,
              backgroundPosition: `${-tileCol * CELL_SIZE}px ${-tileRow * CELL_SIZE}px`,
            }}
          />
        );
      }
      const emptySpacing = mode === "picture" ? "" : "m-0.5 rounded-md";
      return (
        <div
          key={`${i}-${j}`}
          style={{ width: CELL_SIZE, height: CELL_SIZE }}
          className={`${emptySpacing} bg-gray-900 opacity-30`}
        />
      );
    }

    const canClick = !complete;
    const tileRow = Math.floor((col - 1) / 3);
    const tileCol = (col - 1) % 3;
    const isPicture = mode === "picture";
    const spacing = isPicture ? "m-0" : "m-0.5 rounded-md";
    const base = `${spacing} flex items-center justify-center select-none transition-all duration-150 ${
      complete ? "cursor-not-allowed" : "cursor-pointer"
    }`;

    if (isPicture && PUZZLE_IMAGE_URL) {
      return (
        <div
          key={`${i}-${j}`}
          className={`${base} ${canClick ? "hover:brightness-110 active:scale-95" : ""}`}
          style={{
            width: CELL_SIZE,
            height: CELL_SIZE,
            backgroundImage: `url(${PUZZLE_IMAGE_URL})`,
            backgroundSize: `${3 * CELL_SIZE}px ${3 * CELL_SIZE}px`,
            backgroundPosition: `${-tileCol * CELL_SIZE}px ${-tileRow * CELL_SIZE}px`,
          }}
          onClick={() => movePiece(i, j)}
        />
      );
    }

    if (isPicture && !PUZZLE_IMAGE_URL) {
      const hue = (col - 1) * 40;
      return (
        <div
          key={`${i}-${j}`}
          className={`${base} ${canClick ? "hover:opacity-90 active:scale-95" : ""}`}
          style={{
            width: CELL_SIZE,
            height: CELL_SIZE,
            backgroundColor: `hsl(${hue}, 60%, 45%)`,
          }}
          onClick={() => movePiece(i, j)}
        >
          <span className="text-white text-2xl font-bold">{col}</span>
        </div>
      );
    }

    return (
      <div
        key={`${i}-${j}`}
        className={`${base} bg-gray-300 ${canClick ? "hover:bg-gray-200 active:scale-95" : ""}`}
        style={{ width: CELL_SIZE, height: CELL_SIZE }}
        onClick={() => movePiece(i, j)}
      >
        <span className="text-gray-800 text-2xl font-bold">{col}</span>
      </div>
    );
  };

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto my-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Slider Puzzle</h1>
        <p className="text-gray-400 mb-4">
          Slide tiles into order. Goal: arrange 1–8 in sequence with the blank at the end.
        </p>

        <div className="inline-flex rounded-lg overflow-hidden border border-gray-600 mb-6">
          <button
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === "numbers"
                ? "bg-blue-600 text-white"
                : "bg-gray-800 text-gray-400 hover:bg-gray-700"
            }`}
            onClick={() => setMode("numbers")}
          >
            Numbers
          </button>
          <button
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === "picture"
                ? "bg-blue-600 text-white"
                : "bg-gray-800 text-gray-400 hover:bg-gray-700"
            }`}
            onClick={() => setMode("picture")}
          >
            Picture
          </button>
        </div>

        <div className="inline-block bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-400 text-sm">
              Moves: <span className="text-white font-bold text-base">{moves}</span>
            </span>
            <button
              className="bg-blue-500 text-white py-1.5 px-4 rounded hover:bg-blue-600 focus:outline-none transition-colors text-sm"
              onClick={resetPuzzle}
            >
              {phase === "preview" ? "Scramble & Play" : "New Game"}
            </button>
          </div>

          <div
            className={`rounded-lg overflow-hidden transition-all ${
              mode === "picture" ? "" : "p-1 bg-gray-700"
            } ${complete ? "ring-2 ring-green-400" : ""}`}
          >
            {puzzle.map((row, i) => (
              <div key={i} className="flex">
                {row.map((col, j) => renderTile(col, i, j))}
              </div>
            ))}
          </div>

          {complete && (
            <div className="mt-4">
              <p className="text-green-400 font-bold text-lg">Puzzle Solved!</p>
              <p className="text-gray-400 text-sm">Completed in {moves} moves</p>
            </div>
          )}
        </div>

        {mode === "picture" && !PUZZLE_IMAGE_URL && (
          <p className="mt-3 text-gray-500 text-xs">
            Picture mode — image not yet set. Showing color placeholders until{" "}
            <code className="text-gray-400">PUZZLE_IMAGE_URL</code> is configured.
          </p>
        )}
      </div>
    </div>
  );
}
